import { ChangeEvent, useEffect } from 'react';
import {
  FieldErrors,
  RegisterOptions,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormResetField,
  UseFormSetError,
  UseFormSetValue,
  FieldValues,
} from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { AddressSharedServiceI } from '@msc-b2c/interfaces/interfaces';
import {
  AppDispatch,
  AddressSharedService,
  openLoader,
  closeLoader,
  setAddressData,
} from '@msc-b2c/shared-utils';

import { Button } from '../../../button/Button';
import { InputText } from '../../input/InputText';

import styles from './zipcodeinput.module.scss';

type ZipCodeInputProps = {
  disabled?: boolean;
  label: string;
  name: string;
  isLowerLabel?: boolean;
  index: number;
  placeholder: string;
  verifyZipCodeButtonLabel?: string;
  zipCodeValidationErrorMessage?: string;
  additionalValidationObj?: FieldValues;
  clearErrors: UseFormClearErrors<FieldValues>;
  errors: FieldErrors;
  setError: UseFormSetError<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  register: UseFormRegister<FieldValues>;
  resetField: UseFormResetField<FieldValues>;
  validation?: RegisterOptions;
  selectedCountry: string;
  styleOptions?: string;
};

export function ZipCodeInput({
  disabled,
  isLowerLabel,
  index,
  label,
  name,
  placeholder,
  verifyZipCodeButtonLabel,
  zipCodeValidationErrorMessage = '',
  clearErrors,
  errors,
  setError,
  getValues,
  setValue,
  register,
  resetField,
  validation,
  selectedCountry,
  styleOptions,
}: Readonly<ZipCodeInputProps>) {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    resetField(name, { keepDirty: false, keepError: false, keepTouched: false });
  }, [selectedCountry]);

  const handleValidateZipCode = async () => {
    const postCodeValue = getValues(name);
    if (!postCodeValue?.trim()) {
      return;
    }

    const zipCodeService = new AddressSharedService(false, {
      countryCode: selectedCountry?.trim() ?? '',
      postCode: postCodeValue.trim(),
    });

    openLoader('.loader');
    try {
      const addressData = await zipCodeService.getData();

      if (!addressData?.addresses?.length) {
        closeLoader('.loader');
        setError(name, {
          type: 'manual',
          message: zipCodeValidationErrorMessage,
        });
        return;
      }

      dispatch(
        setAddressData(
          addressData.addresses.filter((address: AddressSharedServiceI) => !!address?.town)
        )
      );
    } catch (err) {
      console.error('Error: ', err);
      closeLoader('.loader');
      setError(name, {
        type: 'manual',
        message: zipCodeValidationErrorMessage,
      });
    }

    closeLoader('.loader');
  };

  const handleChange = (value: string) => {
    dispatch(setAddressData([]));
    setValue(name, value, { shouldValidate: true });
  };

  return (
    <div className={styles['cmp-form-text__zip-code-wrapper']}>
      <InputText
        disabled={disabled}
        index={index}
        isLowerLabel={isLowerLabel}
        label={label}
        name={name}
        placeholder={placeholder}
        clearErrors={clearErrors}
        errors={errors}
        register={register}
        validation={validation}
        handleInputChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
        styleOptions={`cmp-form-text__zip-code ${styleOptions}`}
      />
      <Button
        id={`button-form-zipcode`}
        text={verifyZipCodeButtonLabel}
        onClick={handleValidateZipCode}
        styleOption="cmp-button__primary cmp-button__verify-zip-code"
        disabled={!selectedCountry || !getValues(name)}
      />
    </div>
  );
}
